import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCoupon, useSimilarCoupons } from '../../hooks/useCoupons';
import { useCouponReport } from '../../hooks/useCouponReport';
import RedemptionModal from '../../components/coupon/RedemptionModal';
import CouponCard from '../../components/coupon/CouponCard';
import VoteButton from '../../components/coupon/VoteButton';
import CommentsSection from '../../components/coupon/CommentsSection';
import { LoadingPage } from '../../components/common/UI';
import MerchantLogo from '../../components/common/MerchantLogo';
import { formatDiscount, expiryLabel, daysUntilExpiry } from '../../utils/helpers';

export default function CouponDetailPage() {
  const { id } = useParams();
  const { coupon, loading, error } = useCoupon(id);
  const { coupons: similar, loading: similarLoading } = useSimilarCoupons(id, 6);
  const [showModal, setShowModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [shared, setShared] = useState(false);

  if (loading) return <LoadingPage />;
  if (error || !coupon) return (
    <div style={{ padding: '96px 20px', textAlign: 'center', maxWidth: 520, margin: '0 auto' }}>
      <p style={{ fontSize: 64, marginBottom: 20 }}>😕</p>
      <h2 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 10 }}>Deal not found</h2>
      <p style={{ fontSize: 15, color: '#4A5568', marginBottom: 28 }}>It may have expired or been removed. Check out our latest deals.</p>
      <Link to="/coupons" className="btn btn-primary btn-lg">Browse all deals →</Link>
    </div>
  );

  const days = daysUntilExpiry(coupon.expires_at);
  const isExpiring = days !== null && days <= 7 && days >= 0;
  const usageRatio = coupon.max_uses && coupon.used_count != null ? coupon.used_count / coupon.max_uses : null;
  const discount = formatDiscount(coupon);

  const handleShare = async () => {
    const url = window.location.href;
    const data = { title: coupon.title, text: `${discount} at ${coupon.merchant?.name} — via PerkVora`, url };
    if (navigator.share) {
      try { await navigator.share(data); } catch {}
    } else {
      navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    }
  };

  return (
    <div style={{ background: '#F7F8FC' }}>
      {/* Hero banner */}
      <div style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '36px 0 120px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-30%', right: '-10%', width: 420, height: 420, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,70,239,0.3) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative' }}>
          <Link to="/coupons" style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 18, fontWeight: 500 }}>
            ← Back to deals
          </Link>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            {coupon.merchant && (
              <MerchantLogo merchant={coupon.merchant} size={64} radius={14}
                style={{ border: '1px solid rgba(255,255,255,0.2)' }} />
            )}
            <div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {coupon.category && <span className="chip">{coupon.category.name}</span>}
                {coupon.is_featured && <span className="chip" style={{ background: 'var(--brand-gradient)', border: 'none' }}>⭐ Featured</span>}
                {coupon.is_exclusive && <span className="chip" style={{ background: 'rgba(245,158,11,0.25)', borderColor: 'rgba(245,158,11,0.4)' }}>🔒 Exclusive</span>}
                {isExpiring && <span className="chip" style={{ background: 'rgba(220,38,38,0.25)', borderColor: 'rgba(220,38,38,0.4)' }}>🔥 Ends soon</span>}
              </div>
              <p style={{ fontSize: 14, fontWeight: 600, opacity: 0.85 }}>{coupon.merchant?.name}</p>
            </div>
          </div>
          <h1 style={{ fontSize: 'clamp(26px, 3.8vw, 40px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.15, marginTop: 16, maxWidth: 800 }}>{coupon.title}</h1>
        </div>
      </div>

      {/* Content */}
      <div className="container" style={{ marginTop: -80, position: 'relative', paddingBottom: 72 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 32, alignItems: 'start' }} className="detail-grid">

          {/* LEFT — detail */}
          <div>
            {/* Discount hero card */}
            <div style={{ background: '#fff', borderRadius: 20, padding: 36, border: '1px solid #E4E7F0', boxShadow: '0 12px 32px rgba(15,18,37,0.06)', marginBottom: 20 }}>
              <div style={{ background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)', borderRadius: 16, padding: '36px 24px', textAlign: 'center', marginBottom: 24 }}>
                <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6B2FD9', marginBottom: 10 }}>Your discount</p>
                <p className="gradient-text" style={{ fontSize: 'clamp(44px, 7vw, 72px)', fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>{discount}</p>
                {coupon.min_order_amount && <p style={{ fontSize: 14, color: '#4A5568', marginTop: 12 }}>on orders over <strong>${coupon.min_order_amount}</strong></p>}
                {coupon.max_discount_amount && <p style={{ fontSize: 13, color: '#8B94A8', marginTop: 4 }}>Up to ${coupon.max_discount_amount} max discount</p>}
              </div>

              {coupon.description && (
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B2FD9', marginBottom: 10 }}>About this deal</h3>
                  <p style={{ fontSize: 15, color: '#0F1225', lineHeight: 1.7 }}>{coupon.description}</p>
                </div>
              )}

              {/* Info grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                <InfoChip icon="⏰" label="Expires" value={expiryLabel(coupon.expires_at)} warn={days !== null && days <= 3} />
                {coupon.max_uses && <InfoChip icon="🎫" label="Uses" value={`${coupon.used_count}/${coupon.max_uses}`} />}
                {coupon.is_online   && <InfoChip icon="🌐" label="Online" value="Available" />}
                {coupon.is_in_store && <InfoChip icon="📍" label="In-store" value="Available" />}
              </div>

              {/* Usage bar */}
              {usageRatio !== null && (
                <div style={{ marginTop: 20, padding: '14px 18px', background: '#F7F8FC', borderRadius: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#4A5568', fontWeight: 600, marginBottom: 6 }}>
                    <span>Claimed by members</span>
                    <span>{Math.round(usageRatio * 100)}% used</span>
                  </div>
                  <div style={{ height: 6, background: '#E4E7F0', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${Math.min(usageRatio * 100, 100)}%`, background: usageRatio > 0.8 ? 'var(--danger)' : 'var(--brand-gradient)', transition: 'width 0.3s' }} />
                  </div>
                </div>
              )}
            </div>

            {/* How to use */}
            <div style={{ background: '#fff', borderRadius: 20, padding: 32, border: '1px solid #E4E7F0', marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 20 }}>How to redeem</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <HowStep n="1" text={coupon.code_type === 'affiliate_link' ? 'Click "Get This Deal" to activate cashback' : 'Click "Get This Deal" to reveal your code'} />
                <HowStep n="2" text={coupon.code_type === 'affiliate_link' ? `Shop at ${coupon.merchant?.name} in the same session` : `Shop at ${coupon.merchant?.name} and paste your code at checkout`} />
                <HowStep n="3" text={coupon.code_type === 'affiliate_link' ? 'Discount and cashback apply automatically' : 'Your discount applies instantly — enjoy the savings!'} />
              </div>
            </div>

            {/* Terms */}
            {coupon.terms && (
              <div style={{ background: '#fff', borderRadius: 20, padding: 32, border: '1px solid #E4E7F0' }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 14 }}>Terms & Conditions</h3>
                <p style={{ fontSize: 14, color: '#4A5568', lineHeight: 1.75 }}>{coupon.terms}</p>
              </div>
            )}
          </div>

          {/* RIGHT — sticky CTA */}
          <div>
            <div style={{ position: 'sticky', top: 92 }}>
              <div style={{ background: '#fff', borderRadius: 20, padding: 28, border: '1px solid #E4E7F0', boxShadow: '0 12px 32px rgba(15,18,37,0.08)' }}>

                {/* Merchant card */}
                {coupon.merchant && (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', paddingBottom: 20, borderBottom: '1px solid #F0F2F8', marginBottom: 20 }}>
                    <MerchantLogo merchant={coupon.merchant} size={52} radius={12} />
                    <div>
                      <p style={{ fontWeight: 700, fontSize: 15 }}>{coupon.merchant.name}</p>
                      {coupon.merchant.is_verified && <p style={{ fontSize: 12, color: '#0F9D6E', fontWeight: 600 }}>✓ Verified merchant</p>}
                    </div>
                  </div>
                )}

                {/* Main CTA */}
                <button className="btn btn-primary btn-lg btn-full" style={{ marginBottom: 10 }} onClick={() => setShowModal(true)}>
                  🎁 Get This Deal →
                </button>

                {/* Vote */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                  <VoteButton coupon={coupon} size="lg" />
                </div>

                {coupon.merchant?.website && (
                  <a href={coupon.merchant.website} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-full" style={{ marginBottom: 16 }}>
                    Visit {coupon.merchant.name} ↗
                  </a>
                )}

                {/* Meta */}
                <div style={{ fontSize: 12, color: '#8B94A8', lineHeight: 1.8, marginBottom: 16, padding: '14px 16px', background: '#F7F8FC', borderRadius: 10 }}>
                  {coupon.min_order_amount && <div>💵 Min. order: <strong style={{ color: '#0F1225' }}>${coupon.min_order_amount}</strong></div>}
                  {coupon.max_discount_amount && <div>💰 Max. discount: <strong style={{ color: '#0F1225' }}>${coupon.max_discount_amount}</strong></div>}
                  {coupon.max_uses_per_user && <div>🧑 Per user: <strong style={{ color: '#0F1225' }}>{coupon.max_uses_per_user} use(s)</strong></div>}
                  {coupon.verified_at && <div style={{ color: '#0F9D6E', fontWeight: 600 }}>✓ Verified recently</div>}
                </div>

                {/* Share */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleShare}
                    style={{ flex: 1, padding: '10px 14px', background: 'transparent', border: '1.5px solid #E4E7F0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#4A5568', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F7F8FC'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    {shared ? '✓ Copied!' : '↗ Share'}
                  </button>
                  <button onClick={() => setShowReportModal(true)}
                    style={{ flex: 1, padding: '10px 14px', background: 'transparent', border: '1.5px solid #E4E7F0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#DC2626', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#FDECEA'; e.currentTarget.style.borderColor = '#DC2626'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#E4E7F0'; }}
                  >
                    ⚠ Report
                  </button>
                </div>

                {coupon.ftc_disclosure && (
                  <p className="ftc-notice" style={{ textAlign: 'center', marginTop: 14, fontSize: 11 }}>
                    *Affiliate link — we may earn a commission.
                  </p>
                )}
              </div>

              {/* Trust strip */}
              <div style={{ marginTop: 16, padding: '16px 20px', background: 'linear-gradient(135deg, #F3EEFF 0%, #FFE8D6 100%)', borderRadius: 14, fontSize: 12, color: '#4A5568', lineHeight: 1.6 }}>
                <p style={{ fontWeight: 700, color: '#0F1225', marginBottom: 4 }}>🛡️ PerkVora verified</p>
                <p>Every code is tested before going live. If this deal doesn't work, we'll refund your cashback and take it down.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments section (#6 Community v2) */}
      <CommentsSection couponId={coupon.id} />

      {/* Similar deals rail — peer co-redemption + same merchant/category. */}
      {(similarLoading || similar.length > 0) && (
        <section style={{ background: '#fff', borderTop: '1px solid #E4E7F0', padding: '56px 0 72px' }}>
          <div className="container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6B2FD9', marginBottom: 6 }}>You might also like</p>
                <h2 style={{ fontSize: 'clamp(22px, 2.6vw, 30px)', fontWeight: 800, letterSpacing: '-0.025em' }}>Similar deals</h2>
              </div>
              <Link to="/coupons" style={{ fontSize: 14, fontWeight: 600, color: '#6B2FD9', textDecoration: 'none' }}>
                Browse all →
              </Link>
            </div>
            {similarLoading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                {[...Array(3)].map((_, i) => (
                  <div key={i} style={{ height: 220, background: '#F7F8FC', borderRadius: 16 }} />
                ))}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                {similar.map(c => <CouponCard key={c.id} coupon={c} />)}
              </div>
            )}
          </div>
        </section>
      )}

      <style>{`
        @media (max-width: 880px) {
          .detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {showModal && <RedemptionModal coupon={coupon} onClose={() => setShowModal(false)} />}
      {showReportModal && <ReportBrokenModal couponId={coupon.id} onClose={() => setShowReportModal(false)} />}
    </div>
  );
}

function ReportBrokenModal({ couponId, onClose }) {
  const { reportBroken, submitting, submitted, error } = useCouponReport();
  const [reportType, setReportType] = useState('code_not_working');
  const [description, setDescription] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    await reportBroken(couponId, reportType, description);
  };

  if (submitted) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-box" onClick={e => e.stopPropagation()} style={{ overflow: 'hidden', maxWidth: 440 }}>
          <div style={{ padding: '40px 28px', textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 32, color: '#fff' }}>✓</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8 }}>Thanks for the report!</h2>
            <p style={{ fontSize: 14, color: '#4A5568', lineHeight: 1.6, marginBottom: 24 }}>Our team will review this within a few hours. If others confirm, we'll auto-deactivate the deal.</p>
            <button onClick={onClose} className="btn btn-primary btn-full">Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ overflow: 'hidden', maxWidth: 480 }}>
        <div style={{ background: 'linear-gradient(135deg, #1A1B4B 0%, #DC2626 130%)', color: '#fff', padding: '24px 28px', position: 'relative' }}>
          <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.18)', border: 'none', width: 32, height: 32, borderRadius: '50%', color: '#fff', fontSize: 18, cursor: 'pointer' }}>×</button>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.9, marginBottom: 4 }}>Help keep deals fresh</p>
          <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>Report this deal</h2>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 28 }}>
          {error && <div className="alert alert-danger">{error}</div>}

          <p style={{ fontSize: 14, color: '#4A5568', marginBottom: 18, lineHeight: 1.6 }}>
            What's wrong with this deal? Your report helps every other shopper.
          </p>

          <div className="form-group">
            <label className="form-label">What happened?</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { v: 'code_not_working', label: '🚫 Code didn\'t work at checkout' },
                { v: 'expired',          label: '⏰ Deal seems expired' },
                { v: 'missing_discount', label: '💸 Discount didn\'t apply' },
                { v: 'wrong_terms',      label: '📋 Terms are inaccurate' },
                { v: 'other',            label: '❓ Other issue' },
              ].map(opt => (
                <label key={opt.v} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
                  border: `1.5px solid ${reportType === opt.v ? '#6B2FD9' : '#E4E7F0'}`,
                  background: reportType === opt.v ? '#F3EEFF' : '#fff',
                  borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 500,
                  color: reportType === opt.v ? '#6B2FD9' : '#0F1225', transition: 'all 0.15s',
                }}>
                  <input type="radio" name="reportType" value={opt.v} checked={reportType === opt.v}
                    onChange={e => setReportType(e.target.value)}
                    style={{ accentColor: '#6B2FD9' }} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">More details <span style={{ color: '#8B94A8', fontWeight: 400 }}>(optional)</span></label>
            <textarea className="form-input" rows={3} value={description} onChange={e => setDescription(e.target.value)}
              placeholder="What happened when you tried to use it?" />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn-secondary btn-lg" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-lg" disabled={submitting} style={{ flex: 2 }}>
              {submitting ? 'Submitting…' : 'Submit report →'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function InfoChip({ icon, label, value, warn }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: '#F7F8FC', borderRadius: 10 }}>
      <div style={{ fontSize: 18, flexShrink: 0 }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <p style={{ fontSize: 10, fontWeight: 700, color: '#8B94A8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</p>
        <p style={{ fontSize: 13, fontWeight: 700, color: warn ? '#DC2626' : '#0F1225', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</p>
      </div>
    </div>
  );
}

function HowStep({ n, text }) {
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--brand-gradient)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, flexShrink: 0, fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>{n}</div>
      <p style={{ fontSize: 14, color: '#0F1225', lineHeight: 1.55 }}>{text}</p>
    </div>
  );
}
